import React from "react";
import Panel from "./Panel";
import ThemeAndExtras from "./ThemeAndExtras";
import {
  FolderTree, Gauge, Mail, Trophy, Star, MessageSquare, BookOpen,
  Music, Headphones, Guitar, Radio, Disc3, Mic2
} from "lucide-react";

const GENRES = [
  { name: "Pop", icon: Music },
  { name: "Rock", icon: Guitar },
  { name: "Hip-Hop", icon: Mic2 },
  { name: "R&B/Soul", icon: Disc3 },
  { name: "Electronic", icon: Radio },
  { name: "Metal", icon: Headphones },
  { name: "Country", icon: Music },
  { name: "Jazz/Oldies", icon: Disc3 },
];

export default function LeftSidebar({
  onGenreSelect, stats, onAbout, onGuestbook, onTellFriend,
  theme, onThemeChange, onReplayBoot, onOpenMixtape,
}) {
  return (
    <div className="flex flex-col gap-2">
      <Panel data-testid="panel-categories" title="Categories" icon={FolderTree} variant="blue">
        <ul className="text-[12px] space-y-0.5">
          {GENRES.map((g) => {
            const Icon = g.icon;
            return (
              <li key={g.name}>
                <button
                  data-testid={`genre-${g.name}`}
                  onClick={() => onGenreSelect?.(g.name)}
                  className="w-full text-left flex items-center gap-1.5 px-1 py-0.5 hover:bg-[#316AC5] hover:text-white group"
                >
                  <span className="text-[#555] group-hover:text-[#FFEB3B]">▸</span>
                  <Icon size={11} className="shrink-0" />
                  <span className="text-[#003c9e] underline group-hover:text-white group-hover:no-underline">
                    {g.name}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </Panel>

      <Panel data-testid="panel-stats" title="My Network Status" icon={Gauge} variant="green">
        <div className="space-y-1.5 text-[11px]">
          <StatLine label="Status" value={<><span className="led-green pulse-green inline-block" /> <b className="text-[#1F8020] ml-1">ONLINE</b></>} />
          <StatLine label="Peers" value={<b>{(stats?.peers_online || 248392).toLocaleString()}</b>} />
          <StatLine label="Shared" value={<b>{(stats?.shared_files || 4923348).toLocaleString()}</b>} />
          <StatLine label="Speed" value={<b className="text-[#1F8020]">{stats?.network_speed_kbps || 334} KB/s</b>} />
          <StatLine label="Uptime" value={<b>{stats?.uptime_days ?? 97} days</b>} />
          <StatLine label="Ratio" value={<b className="text-[#003c9e]">2.14 &uarr;</b>} />
          <div className="border-t border-[#9C9A7E] my-1" />
          <div className="flex items-center justify-between">
            <span>Bandwidth</span>
            <span className="font-mono text-[10px]">72% / 100%</span>
          </div>
          <div className="xp-progress" style={{ height: 8 }}>
            <div className="xp-progress-fill" style={{ width: "72%" }} />
          </div>
        </div>
      </Panel>

      <Panel data-testid="panel-actions" title="Quick Actions" icon={Star} variant="lemon">
        <div className="space-y-1.5">
          <SidebarBtn data-testid="action-tell" icon={Mail} onClick={onTellFriend}>Tell a Friend</SidebarBtn>
          <SidebarBtn data-testid="action-guestbook" icon={BookOpen} onClick={onGuestbook}>Sign Guestbook</SidebarBtn>
          <SidebarBtn data-testid="action-about" icon={MessageSquare} onClick={onAbout}>About Us</SidebarBtn>
          <SidebarBtn data-testid="action-awards" icon={Trophy}>Site Awards</SidebarBtn>
          <div className="border-t border-[#9C9A7E] my-1.5" />
          <ThemeAndExtras
            theme={theme}
            onThemeChange={onThemeChange}
            onReplayBoot={onReplayBoot}
            onOpenMixtape={onOpenMixtape}
          />
        </div>
      </Panel>
    </div>
  );
}

function StatLine({ label, value }) {
  return (
    <div className="flex items-center justify-between text-[12px]">
      <span className="text-[#333]">{label}:</span>
      <span className="text-[#222]">{value}</span>
    </div>
  );
}

function SidebarBtn({ icon: Icon, children, className = "", ...props }) {
  return (
    <button
      {...props}
      className={`bevel-btn w-full text-left px-2 py-1.5 text-[12px] flex items-center gap-1.5 ${className}`}
    >
      <Icon size={12} />
      {children}
    </button>
  );
}
